import type { HireReference, HireReferences, PeopleWorker } from '../operations/model';

export const clearStaffingValue = '__CLEAR__';
export const staffingChangeFields = [
  { key: 'organizationId', label: '조직', inputLabel: '이동 조직', previewField: 'organization', previewLabel: '소속 변경' },
  { key: 'locationId', label: '근무지', inputLabel: '이동 근무지', previewField: 'location', previewLabel: '근무지 변경' },
  { key: 'jobProfileId', label: '직무', inputLabel: '이동 직무', previewField: 'job', previewLabel: '직무 변경' },
  { key: 'jobLevelId', label: '직무 레벨', inputLabel: '직무 레벨', previewField: 'jobLevel', previewLabel: '직무 레벨 변경' },
  { key: 'jobTypeId', label: '고용 유형', inputLabel: '고용 유형', previewField: 'jobType', previewLabel: '고용 유형 변경' },
  { key: 'timeType', label: '근무 형태', inputLabel: '근무 형태', previewField: 'timeType', previewLabel: '근무 형태 변경' },
  { key: 'workSpaceId', label: '업무 공간', inputLabel: '업무 공간', previewField: 'workSpace', previewLabel: '업무 공간 변경' },
  { key: 'managerId', label: '보고 관리자', inputLabel: '보고 관리자', previewField: 'manager', previewLabel: '보고 관리자 변경' },
  { key: 'businessTitle', label: '직함', inputLabel: '변경할 직함', previewField: 'title', previewLabel: '직함 변경' },
] as const;
export type StaffingChangeField = typeof staffingChangeFields[number]['key'];
export type StaffingChangeDraft = Partial<Record<StaffingChangeField, string>>;
export type StaffingChangeDrafts = Record<string, StaffingChangeDraft>;
export type StaffingWorkerType = 'EMPLOYEE' | 'CONTINGENT_WORKER';
export type PeopleStaffingReferences = HireReferences & {
  jobTypes?: Partial<Record<StaffingWorkerType, HireReference[]>>;
  workSpaces?: Record<string, HireReference[]>;
  managers?: HireReference[];
};
export const staffingWorkerType = (worker: PeopleWorker): StaffingWorkerType => worker.workerTypeId === 'CONTINGENT_WORKER' ? 'CONTINGENT_WORKER' : 'EMPLOYEE';
export const staffingSelectedFields = (draft: StaffingChangeDraft) => staffingChangeFields.filter(field => Object.hasOwn(draft, field.key));
export const staffingLocations = (workers: PeopleWorker[], draft: StaffingChangeDraft) => [...new Set(workers.map(worker => draft.locationId || worker.locationId || ''))];
export function staffingFieldLabel(field: StaffingChangeField, workers: PeopleWorker[]) {
  return field === 'jobTypeId' && workers.length > 0 && workers.every(worker => staffingWorkerType(worker) === 'CONTINGENT_WORKER') ? '외부 인력 유형' : staffingChangeFields.find(item => item.key === field)!.label;
}
export function staffingOptionsFor(field: StaffingChangeField, references?: PeopleStaffingReferences, workers: PeopleWorker[] = [], draft: StaffingChangeDraft = {}) {
  if (field === 'organizationId') return references?.organizations ?? [];
  if (field === 'locationId') return references?.locations ?? [];
  if (field === 'jobProfileId') return references?.jobs ?? [];
  if (field === 'jobLevelId') return references?.levels ?? [];
  if (field === 'managerId') return [{ id: clearStaffingValue, descriptor: '보고 관리자 해제' }, ...(references?.managers ?? []).filter(item => !workers.some(worker => worker.id === item.id))];
  if (field === 'timeType') return [{ id: 'FULL_TIME', descriptor: '전일제' }, { id: 'PART_TIME', descriptor: '시간제' }];
  if (field === 'jobTypeId') {
    const types = [...new Set(workers.map(staffingWorkerType))];
    return types.length === 1 ? references?.jobTypes?.[types[0]] ?? [] : [];
  }
  if (field === 'workSpaceId') {
    const locations = staffingLocations(workers, draft);
    return [{ id: clearStaffingValue, descriptor: '업무 공간 해제' }, ...(locations.length === 1 ? references?.workSpaces?.[locations[0]] ?? [] : [])];
  }
  return [];
}
export function getStaffingCurrentValue(worker: PeopleWorker, field: StaffingChangeField) {
  const raw = field === 'organizationId' ? worker.organization : field === 'locationId' ? worker.location : field === 'jobProfileId' ? worker.jobProfile
    : field === 'jobLevelId' ? worker.jobLevel : field === 'jobTypeId' ? worker.jobType : field === 'workSpaceId' ? worker.workSpace
      : field === 'managerId' ? worker.manager : field === 'timeType' ? (worker.timeType === 'FULL_TIME' ? '전일제' : worker.timeType === 'PART_TIME' ? '시간제' : undefined) : worker.businessTitle;
  const value = raw?.trim();
  return !value || value === '—' || (field === 'organizationId' && value === '미배치') ? '' : value;
}
export function staffingNextValue(field: StaffingChangeField, value: string | undefined, references?: PeopleStaffingReferences, workers: PeopleWorker[] = [], draft: StaffingChangeDraft = {}) {
  if (value === clearStaffingValue && (field === 'businessTitle' || field === 'workSpaceId' || field === 'managerId')) return '없음';
  return field === 'businessTitle' ? value?.trim() : staffingOptionsFor(field, references, workers, draft).find(option => option.id === value)?.descriptor;
}
export function isStaffingDraftValid(draft: StaffingChangeDraft, references?: PeopleStaffingReferences, workers: PeopleWorker[] = [], commonPatch = false) {
  const fields = staffingSelectedFields(draft);
  if (!commonPatch && draft.locationId && workers.some(worker => worker.locationId !== draft.locationId && Boolean(worker.workSpaceId || worker.workSpace)) && !draft.workSpaceId) return false;
  return fields.length > 0 && fields.every(({ key }) => key === 'businessTitle' ? Boolean(draft[key]?.trim())
    : Boolean(draft[key]?.trim()) && staffingOptionsFor(key, references, workers, draft).some(option => option.id === draft[key]));
}
export function updateStaffingDraft(draft: StaffingChangeDraft, field: StaffingChangeField, value: string, workers: PeopleWorker[], commonPatch = false): StaffingChangeDraft {
  const next = { ...draft, [field]: value };
  if (field === 'locationId' && value !== draft.locationId) {
    delete next.workSpaceId;
    if (!commonPatch && value && workers.some(worker => worker.locationId !== value && Boolean(worker.workSpaceId))) next.workSpaceId = clearStaffingValue;
  }
  return next;
}
export function removeStaffingField(draft: StaffingChangeDraft, field: StaffingChangeField): StaffingChangeDraft {
  const next = { ...draft };
  delete next[field];
  if (field === 'locationId') delete next.workSpaceId;
  return next;
}
export function mergeStaffingDrafts(drafts: StaffingChangeDrafts, ids: readonly string[], patch: StaffingChangeDraft, workers: PeopleWorker[] = []): StaffingChangeDrafts {
  const next = { ...drafts };
  for (const id of ids) {
    const worker = workers.find(item => item.id === id);
    const previous = drafts[id] ?? {};
    const merged = { ...previous, ...patch };
    if (patch.locationId && patch.locationId !== (previous.locationId || worker?.locationId) && !Object.hasOwn(patch, 'workSpaceId')) {
      delete merged.workSpaceId;
      if (worker?.workSpaceId && patch.locationId !== worker.locationId) merged.workSpaceId = clearStaffingValue;
    }
    next[id] = merged;
  }
  return next;
}
export function buildStaffingRows(workers: readonly PeopleWorker[], drafts: StaffingChangeDrafts, date: string, reason: string): Array<Record<string, string>> {
  return workers.map(worker => {
    const draft = drafts[worker.id] ?? {};
    const row: Record<string, string> = { workerId: worker.id, effectiveDate: date, reason: reason.trim() };
    for (const { key } of staffingSelectedFields(draft)) {
      const value = draft[key]?.trim();
      if (value === clearStaffingValue && (key === 'businessTitle' || key === 'workSpaceId' || key === 'managerId')) row[key === 'businessTitle' ? 'clearBusinessTitle' : key === 'managerId' ? 'clearManager' : 'clearWorkSpace'] = 'true';
      else if (value) row[key] = value;
    }
    return row;
  });
}
